// "My Sales" — a seat's own performance, scoped to orders they personally
// accepted or handed off. Deliberately shows counts and item volume rather
// than store revenue, margin, or payouts, which stay owner-only.

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Package, Clock, TrendingUp, Loader2, ShieldCheck } from 'lucide-react';
import { collection, query, where, orderBy, limit, onSnapshot } from 'firebase/firestore';
import { db, auth } from '../../config/firebase';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { useBusinessContext } from '../../hooks/useBusinessContext';
import { cn } from '../../lib/utils';

type Range = 'today' | 'week' | 'month';

const RANGE_LABELS: Record<Range, string> = {
  today: 'Today',
  week: 'This week',
  month: 'This month',
};

function rangeStart(range: Range): Date {
  const start = new Date();
  start.setHours(0, 0, 0, 0);
  if (range === 'week') start.setDate(start.getDate() - start.getDay());
  if (range === 'month') start.setDate(1);
  return start;
}

interface SeatOrder {
  id: string;
  orderNumber?: string;
  status?: string;
  items?: Array<{ quantity?: number; name?: string }>;
  createdAt?: any;
  acceptedAt?: any;
}

export function SeatSalesPage() {
  const navigate = useNavigate();
  const { businessId, isSeat, loading: contextLoading } = useBusinessContext();
  const uid = auth.currentUser?.uid ?? null;

  const [orders, setOrders] = useState<SeatOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState<Range>('today');

  useEffect(() => {
    if (contextLoading) return;
    if (!businessId || !uid) {
      setLoading(false);
      return;
    }

    const unsubscribe = onSnapshot(
      query(
        collection(db, 'orders'),
        where('businessId', '==', businessId),
        where('acceptedBySeatUid', '==', uid),
        orderBy('createdAt', 'desc'),
        limit(200)
      ),
      (snap) => {
        setOrders(snap.docs.map((d) => ({ id: d.id, ...(d.data() as SeatOrder) })));
        setLoading(false);
      },
      (err) => {
        console.error('Failed to load seat sales:', err);
        setLoading(false);
      }
    );

    return unsubscribe;
  }, [businessId, uid, contextLoading]);

  const inRange = useMemo(() => {
    const start = rangeStart(range).getTime();
    return orders.filter((order) => {
      const at = order.acceptedAt?.toDate?.() || order.createdAt?.toDate?.();
      return at ? at.getTime() >= start : false;
    });
  }, [orders, range]);

  const itemsHandled = useMemo(
    () =>
      inRange.reduce(
        (total, order) =>
          total + (order.items || []).reduce((sum, item) => sum + (item.quantity || 1), 0),
        0
      ),
    [inRange]
  );

  const completed = useMemo(
    () => inRange.filter((order) => order.status === 'delivered' || order.status === 'completed').length,
    [inRange]
  );

  if (!contextLoading && !isSeat) {
    navigate('/business/dashboard', { replace: true });
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/business/dashboard')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-lg font-serif text-heading">My Sales</h1>
            <p className="text-xs text-body">Orders you personally handled</p>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6 space-y-4">
        <div className="flex gap-2">
          {(Object.keys(RANGE_LABELS) as Range[]).map((key) => (
            <button
              key={key}
              onClick={() => setRange(key)}
              className={cn(
                'px-3.5 py-1.5 rounded-full text-sm font-medium transition-colors',
                range === key
                  ? 'bg-primary text-white'
                  : 'bg-white text-body border border-gray-200 hover:border-gray-300'
              )}
            >
              {RANGE_LABELS[key]}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-3 gap-3">
          {[
            { label: 'Orders', value: inRange.length, icon: Package },
            { label: 'Items', value: itemsHandled, icon: TrendingUp },
            { label: 'Completed', value: completed, icon: Clock },
          ].map((stat) => (
            <Card key={stat.label}>
              <CardContent className="p-4">
                <stat.icon className="h-4 w-4 text-primary mb-2" />
                <p className="text-2xl font-serif text-heading">{stat.value}</p>
                <p className="text-xs text-body mt-0.5">{stat.label}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        <Card>
          <CardContent className="p-4">
            <h2 className="text-sm font-semibold text-heading mb-3">
              {RANGE_LABELS[range]}'s orders
            </h2>

            {loading ? (
              <div className="py-10 text-center">
                <Loader2 className="h-6 w-6 text-primary animate-spin mx-auto" />
              </div>
            ) : inRange.length === 0 ? (
              <div className="py-10 text-center">
                <Package className="h-8 w-8 text-gray-300 mx-auto mb-3" />
                <p className="text-sm font-medium text-heading">No orders yet</p>
                <p className="text-xs text-body mt-1">
                  Orders you accept will show up here.
                </p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {inRange.map((order) => {
                  const at = order.acceptedAt?.toDate?.() || order.createdAt?.toDate?.();
                  const count = (order.items || []).reduce(
                    (sum, item) => sum + (item.quantity || 1),
                    0
                  );
                  return (
                    <button
                      key={order.id}
                      onClick={() => navigate(`/business/orders/${order.id}`)}
                      className="w-full flex items-center gap-3 py-3 text-left hover:bg-gray-50 -mx-2 px-2 rounded-lg transition-colors"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-heading truncate">
                          #{order.orderNumber || order.id.slice(0, 6).toUpperCase()}
                        </p>
                        <p className="text-xs text-body">
                          {count} item{count === 1 ? '' : 's'}
                          {at ? ` · ${at.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}` : ''}
                        </p>
                      </div>
                      <span className="text-xs font-medium text-body capitalize flex-shrink-0">
                        {(order.status || '').replace(/_/g, ' ')}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        <div className="flex items-start gap-2.5 px-1">
          <ShieldCheck className="h-4 w-4 text-gray-400 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-body leading-relaxed">
            This page counts the orders you handled. Store revenue, payouts, and tax details stay
            with the owner.
          </p>
        </div>
      </main>
    </div>
  );
}

export default SeatSalesPage;
